import { type Token, TokenType } from './tokens'

/**
 * Tokenizes the Minidown source.
 *
 * @param source
 */
export function tokenize(source: string): Token[] {
  const result: Token[] = []

  // Position in the stream
  let pos = 0

  // The end of the stream
  const eos = source.length

  // Tracks text token start and end
  let textTokenStart = -1
  let textTokenEnd = -1

  while (pos < eos) {
    const ch: CharacterCodes = source.charCodeAt(pos)
    switch (ch) {
      case CharacterCodes.Asterisk:
      case CharacterCodes.Underscore: {
        maybeFlushTextToken()
        const value = String.fromCharCode(ch)
        pos += value.length

        result.push({
          value,
          type: TokenType.StrongOrEmphasisMarker,
        })

        break
      }
      case CharacterCodes.OpenBracket: {
        maybeFlushTextToken()
        pos++
        result.push({
          type: TokenType.OpenBracket,
          value: '[',
        })
        break
      }
      case CharacterCodes.CloseBracket: {
        maybeFlushTextToken()
        pos++
        result.push({
          type: TokenType.CloseBracket,
          value: ']',
        })
        break
      }
      case CharacterCodes.OpenParen: {
        maybeFlushTextToken()
        pos++
        result.push({
          type: TokenType.OpenParen,
          value: '(',
        })
        break
      }
      case CharacterCodes.CloseParen: {
        maybeFlushTextToken()
        pos++
        result.push({
          type: TokenType.CloseParen,
          value: ')',
        })
        break
      }
      case CharacterCodes.Space:
      case CharacterCodes.Tab: {
        // If we already started buffering text, then include the space in the
        // text token.
        if (textTokenStart !== -1) {
          consumeText(1)
          break
        }

        // Consume until we get non-whitespace
        const whiteSpaceStart = pos
        let whiteSpaceEnd = ++pos

        while (pos < eos) {
          const ch: CharacterCodes = source.charCodeAt(pos)
          if (ch !== CharacterCodes.Space && ch !== CharacterCodes.Tab) {
            // Not whitespace anymore
            break
          }

          whiteSpaceEnd = ++pos
        }

        result.push({
          type: TokenType.Space,
          value: source.slice(whiteSpaceStart, whiteSpaceEnd),
        })

        break
      }
      case CharacterCodes.CarriageReturn: {
        maybeFlushTextToken()
        pos++
        break
      }
      case CharacterCodes.LineFeed: {
        maybeFlushTextToken()
        pos++
        result.push({
          type: TokenType.Newline,
          value: '\n',
        })
        break
      }
      case CharacterCodes.Backslash: {
        // Backslashes can be escape sequences.
        const nextPos = pos + 1
        if (nextPos >= eos) {
          // No more tokens, so add the backslash as text
          consumeText(1)
          break
        }

        // Look ahead to what the next character is to see if we should
        // escape it, or if we should treat it as a text token
        const next: CharacterCodes = source.charCodeAt(nextPos)
        switch (next) {
          case CharacterCodes.Asterisk:
          case CharacterCodes.Underscore:
          case CharacterCodes.Backslash:
          case CharacterCodes.OpenBracket:
          case CharacterCodes.CloseBracket:
          case CharacterCodes.OpenParen:
          case CharacterCodes.CloseParen: {
            // Treat the backslash as an escape character.
            // Start by flushing any buffered text since we'll be skipping the
            // backslash
            maybeFlushTextToken()
            // Advance to the character we just checked
            pos++
            // Add this token to the text buffer
            consumeText(1)
            continue
          }
        }

        // It was not a character that needed escaping, so
        // add the backslash and the next token as text and advance by those 2
        // characters.
        consumeText(2)
        break
      }

      default:
        // Anything else is text, so buffer it until we reach another token type
        // or until we reach the end.
        consumeText(1)
    }
  }

  // Check for any unflushed text.
  maybeFlushTextToken()

  /**
   * Advances the position by {@link count} and adds the consumed range
   * to the text buffer.
   *
   * @param count
   */
  function consumeText(count: number) {
    if (textTokenStart === -1) {
      // Start of a text token
      textTokenStart = pos
    }

    pos += count
    textTokenEnd = pos
  }

  /**
   * When we've accumulated characters for a text token,
   * separate any leading and trailing whitespace and emit them as tokens.
   */
  function maybeFlushTextToken() {
    if (textTokenStart === -1) {
      // No text tokens available yet.
      return
    }

    let textBuf = source.slice(textTokenStart, textTokenEnd)
    textTokenStart = -1

    let trailingTrimCount = 0
    scanForTrailingWhitespace: for (let i = textBuf.length - 1; i >= 0; i--) {
      const ch: CharacterCodes = textBuf.charCodeAt(i)
      switch (ch) {
        case CharacterCodes.Space:
        case CharacterCodes.Tab:
          trailingTrimCount++
          break
        default:
          // Exit the loop
          break scanForTrailingWhitespace
      }
    }

    // Before we add the text token, extract and trim any trailing whitespace
    let trailingWhiteSpace: string | null = null
    if (trailingTrimCount > 0) {
      trailingWhiteSpace = textBuf.slice(textBuf.length - trailingTrimCount)
      textBuf = textBuf.slice(0, textBuf.length - trailingTrimCount)
    }

    result.push({
      type: TokenType.Text,
      value: textBuf,
    })

    // Add the trailing whitespace token if there is any.
    if (trailingWhiteSpace !== null) {
      result.push({
        type: TokenType.Space,
        value: trailingWhiteSpace,
      })
    }
  }

  return result
}

/**
 * Whether 2 tokens are equal.
 *
 * @param left
 * @param right
 */
export function tokenEquals(left: Token, right: Token) {
  return left.type === right.type && left.value === right.value
}

/**
 * Special character codes.
 */
export const enum CharacterCodes {
  Asterisk = 42,
  Underscore = 95,
  OpenBracket = 91,
  CloseBracket = 93,
  OpenParen = 40,
  CloseParen = 41,
  LineFeed = 10,
  CarriageReturn = 13,
  Tab = 9,
  Space = 32,
  Backslash = 92,
}
